import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import useTreeChanges from 'tree-changes-hook';
import { initializeUtmTracking } from '~/utils/utm';

import { name } from '~/config';

import { usePaymentPlan } from '~/hooks/usePaymentPlan';
import { useAppSelector } from '~/modules/hooks';
import theme from '~/modules/theme';
import { fetchCurrentUser, fetchPlans } from '~/store/actions';

import Footer from '~/components/Footer';
import FullPageLoader from '~/components/FullPageLoader';
import PrivateRoute from '~/components/PrivateRoute';
import PublicRoute from '~/components/PublicRoute';
import NotFound from '~/routes/NotFound';

import { selectUser } from '~/selectors';
import { UserState } from '~/types';
import PaymentFlow from './components/PaymentFlow';
import SidebarLayout from './components/SidebarLayout';
import AccountSetup from './routes/Auth/AccountSetup';
import ForgotPassword from './routes/Auth/ForgotPassword';
import Login from './routes/Auth/Login';
import ResetPassword from './routes/Auth/ResetPassword';
import Signup from './routes/Auth/Signup';
import TokenLogin from './routes/Auth/TokenLogin';
import VerifyEmail from './routes/Auth/VerifyEmail';
import VerifySubscription from './routes/Payment/VerifySubscription';
import Account from './routes/User/Account';
import AddBusiness from './routes/User/AddBusiness';
import AddInvoice from './routes/User/AddInvoice';
import AddEstimate from './routes/User/AddEstimate';
import Billing from './routes/User/Billing';
import Business from './routes/User/Business';
import Client from './routes/User/Client';
import Dashboard from './routes/User/Dashboard';
import Invoice from './routes/User/Invoice';
import Estimate from './routes/User/Estimate';
import Email from './routes/User/Email';

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  opacity: 1 !important;
  position: relative;
  transition: opacity 0.5s;
`;

const Main = styled.main<Pick<UserState, 'isAuthenticated'>>`
  // min-height: 100vh;
`;

function Root() {
  const dispatch = useDispatch();
  usePaymentPlan();
  const user = useAppSelector(selectUser);
  const { changed } = useTreeChanges(user);

  const { isAuthenticated } = user;

  useEffect(() => {
    if (changed('isAuthenticated', true)) {
      toast.success('Hello! And welcome!');
    }
  }, [dispatch, changed]);

  // Initialize UTM tracking on app start
  useEffect(() => {
    initializeUtmTracking();
  }, []);

  // Fetch plans on app initialization
  useEffect(() => {
    dispatch(fetchPlans());
  }, [dispatch]);

  // Fetch current user data on app initialization if user is authenticated
  useEffect(() => {
    if (isAuthenticated && user.token) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, isAuthenticated, user.token]);

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <AppWrapper data-component-name="app">
          <Helmet
            defaultTitle={name}
            defer={false}
            encodeSpecialCharacters
            htmlAttributes={{ lang: 'en-us' }}
            titleAttributes={{ itemprop: 'name', lang: 'en-us' }}
            titleTemplate={`%s | ${name}`}
          >
            <link
              href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,400;0,700;1,400;1,700&display=swap"
              rel="stylesheet"
            />
          </Helmet>
          <Toaster
            position="top-right"
            containerStyle={{ top: 'calc(20px + env(safe-area-inset-bottom))' }}
          />
          <Main isAuthenticated={isAuthenticated}>
            <Routes>
              <Route
                element={
                  <PublicRoute isAuthenticated={isAuthenticated} to="/private/dashboard">
                    <Login />
                  </PublicRoute>
                }
                path="/"
              />
              <Route
                element={
                  <PublicRoute isAuthenticated={isAuthenticated} to="/private/dashboard">
                    <Signup />
                  </PublicRoute>
                }
                path="/signup"
              />
              <Route
                element={
                  <PublicRoute isAuthenticated={isAuthenticated} to="/private/dashboard">
                    <ForgotPassword />
                  </PublicRoute>
                }
                path="/forgotpassword"
              />
              <Route
                element={
                  <PublicRoute isAuthenticated={isAuthenticated} to="/private/dashboard">
                    <VerifyEmail />
                  </PublicRoute>
                }
                path="/verify-email/:token"
              />
              <Route
                element={
                  <PublicRoute isAuthenticated={isAuthenticated} to="/private/dashboard">
                    <ResetPassword />
                  </PublicRoute>
                }
                path="/reset-password/:token"
              />
              <Route element={<TokenLogin />} path="/token-login" />
              <Route
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated} to="/">
                    <SidebarLayout />
                  </PrivateRoute>
                }
                path="/private"
              >
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="invoices" element={<Invoice />} />
                <Route path="estimates" element={<Estimate />} />
                <Route path="clients" element={<Client />} />
                <Route path="emails" element={<Email />} />
                <Route path="billing" element={<Billing />} />
                <Route path="business" element={<Business />} />
                <Route path="account" element={<Account />} />
                <Route path="addbusiness" element={<AddBusiness />} />
              </Route>
              <Route
                path="/private/addinvoice"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated} to="/">
                    <AddInvoice />
                  </PrivateRoute>
                }
              />
              <Route
                path="/private/addestimate"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated} to="/">
                    <AddEstimate />
                  </PrivateRoute>
                }
              />

              <Route
                path="/private/accountsetup"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated} to="/">
                    <AccountSetup />
                  </PrivateRoute>
                }
              />
              <Route
                path="/verify-subscription"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated} to="/">
                    <VerifySubscription />
                  </PrivateRoute>
                }
              />
              <Route element={<NotFound />} path="*" />
            </Routes>
          </Main>
          <Footer />
          <FullPageLoader />
          <PaymentFlow />
        </AppWrapper>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default Root;
